import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Checkbox, CircularProgress, FormControl, FormControlLabel, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from "react-router";
import { useNavigate } from 'react-router-dom';
import { Customer, emptyCustomer } from '../../interfaces/Customer';
import { EducationOptions, SettlementOptions } from '../../interfaces/Provider';
import { api } from '../../services';
import { HighlightedTextField } from "../../services/customElements";
import { extractBankName, extractBirthDateFromPesel, extractMunicipalityCode, extractSexFromPesel, isFloat } from "../../services/utils";
import EmployeesList from '../EmployeesList';
import FinancialsForm from '../FinancialsForm';
import { validateAllFields } from './validation';

const educationValues = Object.values(EducationOptions);
const settlementValues = Object.values(SettlementOptions);
const CustomerAddPage: React.FC = () => {
    const [customer, setCustomer] = useState(emptyCustomer());
    const { enqueueSnackbar } = useSnackbar();
    const [searchParams, setSearchParams] = useSearchParams();
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [isLoading, setIsLoading] = useState(false);
    const [employeesValid, setEmployeesValid] = useState(true);
    const navigate = useNavigate();
    const [deMinimisFields, setDeMinimisFields] = useState<{ [key: string]: string }>({
        deMinimisPLN: customer.deMinimisPLN.toString().replace(".", ","),
        deMinimisEUR: customer.deMinimisEUR.toString().replace(".", ","),
    });
    const [financialsModelChanged, setFinancialsModelChanged] = useState(false);
    const [employeesChanged, setEmployeesChanged] = useState(false);

    const [financialsValid, setFinancialsValidRaw] = useState([false, false, false]);
    const setFinancialsValid = (index: number, valid: boolean) => {
        setFinancialsValidRaw((prev) => {
            const newValid = [...prev];
            newValid[index] = valid;
            return newValid;
        })
    }

    useEffect(() => {
        if (!localStorage.getItem('token')) {
            navigate('/login');
        }
    }, []);

    useEffect(() => {
        const nip = searchParams.get('nip');
        if (nip) {
            setIsLoading(true);
            api.fetchCustomer(nip).then((customer) => {
                setCustomerAndValidate(customer);
                setDeMinimisFields({
                    deMinimisPLN: customer.deMinimisPLN.toString().replace(".", ","),
                    deMinimisEUR: customer.deMinimisEUR.toString().replace(".", ","),
                });
                setFinancialsModelChanged((prev) => !prev);
                setEmployeesChanged((prev) => !prev);
            }).catch((error) => {
                console.error(error);
                enqueueSnackbar('Błąd podczas pobierania klienta', { variant: 'error' });
            }).finally(() => {
                setIsLoading(false);
            });
        }
    }, [])

    const fillFromNipInfo = async () => {
        try {
            setIsLoading(true);
            const nipInfo = await api.getNipInfo(customer.nip);
            setCustomerAndValidate({
                ...customer,
                companyName: nipInfo.name || customer.companyName,
                regon: nipInfo.regon || customer.regon,
                companyOwnerName: nipInfo.firstname || customer.companyOwnerName,
                companyOwnerSurname: nipInfo.lastname || customer.companyOwnerSurname,
                name: nipInfo.firstname || customer.name,
                surname: nipInfo.lastname || customer.surname,
                pkd: nipInfo.PKDs.length > 0 ? nipInfo.PKDs[0].code : customer.pkd,
                companyAddress: {
                    ...customer.companyAddress,
                    city: nipInfo.city || customer.companyAddress.city,
                    streetName: nipInfo.street || customer.companyAddress.streetName,
                    streetNumber: nipInfo.streetNumber || customer.companyAddress.streetNumber,
                    houseNumber: nipInfo.houseNumber || customer.companyAddress.houseNumber,
                    zipCode: nipInfo.postCode || customer.companyAddress.zipCode,
                    voivodeship: nipInfo.state || customer.companyAddress.voivodeship,
                    municipality: nipInfo.community || customer.companyAddress.municipality,
                    municipalityCode: nipInfo.communityCode || customer.companyAddress.municipalityCode,
                    district: nipInfo.county || customer.companyAddress.district,
                },
                companyStartDate: nipInfo.startDate || customer.companyStartDate,
                companyCreationDate: nipInfo.creationDate || customer.companyCreationDate,
                phoneNumber: nipInfo.phone || customer.phoneNumber,
                email: nipInfo.email || customer.email,
                address: {
                    ...customer.address,
                    city: nipInfo.city || customer.address.city,
                    streetName: nipInfo.street || customer.address.streetName,
                    streetNumber: nipInfo.streetNumber || customer.address.streetNumber,
                    zipCode: nipInfo.postCode,
                    voivodeship: nipInfo.state || customer.address.voivodeship,
                    municipality: nipInfo.community || customer.address.municipality,
                    municipalityCode: nipInfo.communityCode || customer.address.municipalityCode,
                    district: nipInfo.county || customer.address.district,
                },
            }
            );
            enqueueSnackbar('Dane z NIP zostały pobrane.', { variant: 'success' });
        } catch (error) {
            enqueueSnackbar('Nie udało się pobrać danych z NIP.', { variant: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const updateComputedFields = (customer: Customer) => {
        if (customer.pesel.length == 11) {
            const birthDate = extractBirthDateFromPesel(customer.pesel);
            const sex = extractSexFromPesel(customer.pesel);
            customer.sex = sex || customer.sex;
            customer.birthDate = birthDate || customer.birthDate
        }
        if (customer.accountNumber.length === 26) {
            const bankName = extractBankName(customer.accountNumber);
            customer.bankName = bankName || customer.bankName
        }
        if (customer.address.municipality.length > 0) {
            const municipalityCode = extractMunicipalityCode(customer.address.district, customer.address.municipality);
            customer.address.municipalityCode = municipalityCode || customer.address.municipalityCode;
        }
        if (customer.companyAddress.municipality.length > 0) {
            const municipalityCode = extractMunicipalityCode(customer.companyAddress.district, customer.companyAddress.municipality);
            customer.companyAddress.municipalityCode = municipalityCode || customer.companyAddress.municipalityCode;
        }
        return customer;
    }

    const setCustomerAndValidate = (newCustomer: Customer) => {
        const updatedCustomer = updateComputedFields(newCustomer);
        setCustomer(updatedCustomer);
        const allErrors = validateAllFields(updatedCustomer, financialsValid, employeesValid);
        setErrors(allErrors);
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setCustomerAndValidate({
            ...customer,
            [name]: value,
        });
    };


    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const allErrors = validateAllFields(customer, financialsValid, employeesValid);
        if (errors.deMinimisPLN || errors.deMinimisEUR) {
            allErrors.deMinimis = 'Błędy w de minimis';
        }

        if (Object.values(allErrors).some((error) => error)) {
            console.error('Form contains errors:', allErrors);
            return;
        }
        setIsLoading(true);
        try {
            await api.addCustomer(customer);
            enqueueSnackbar('Klient dodany pomyślnie', { variant: 'success' });
            setCustomerAndValidate(emptyCustomer());
            setDeMinimisFields({
                deMinimisPLN: '0',
                deMinimisEUR: '0',
            });
            setFinancialsModelChanged((prev) => !prev);
            setEmployeesChanged((prev) => !prev);
            setErrors({});
        } catch (error) {
            console.error(error);
            enqueueSnackbar('Błąd podczas dodawania klienta', { variant: 'error' });
        } finally {
            setSearchParams({});
            setIsLoading(false);
        }
    };

    const setAndValidateDeMinimis = (newFields: { [key: string]: string }) => {
        setDeMinimisFields(newFields);
        if (!isFloat(newFields.deMinimisPLN)) {
            setErrors((prev) => ({
                ...prev,
                deMinimisPLN: 'Wartość musi być liczbą',
            }));
        } else {
            setCustomer((prev) => ({
                ...prev,
                deMinimisPLN: parseFloat(newFields.deMinimisPLN.replace(",", ".")),
            }))
            setErrors((prev) => {
                const { deMinimisPLN, ...rest } = prev;
                return rest;
            });
        }

        if (!isFloat(newFields.deMinimisEUR)) {
            setErrors((prev) => ({
                ...prev,
                deMinimisEUR: 'Wartość musi być liczbą',
            }));
        } else {
            setCustomer((prev) => ({
                ...prev,
                deMinimisEUR: parseFloat(newFields.deMinimisEUR.replace(",", ".")),
            }))
            setErrors((prev) => {
                const { deMinimisEUR, ...rest } = prev;
                return rest;
            });
        }

        if ((isFloat(newFields.deMinimisPLN) && parseFloat(newFields.deMinimisPLN.replace(",", ".")) > 0) || (isFloat(newFields.deMinimisEUR) && parseFloat(newFields.deMinimisEUR.replace(",", ".")) > 0)) {
            setCustomer((prev) => ({
                ...prev,
                usesDeMinimis: true,
            }))
        } else {
            setCustomer((prev) => ({
                ...prev,
                usesDeMinimis: false,
            }))
        }
    }

    return (
        <>
            <Box sx={{
                display: isLoading ? 'flex' : 'none',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1000,
                backgroundColor: 'rgba(255, 255, 255, 0.53)' // optional semi-transparent backdrop
            }}>
                <CircularProgress />
            </Box>
            <Box sx={{
                padding: 2, maxWidth: 1200, margin: 'auto', display: 'flex', flexDirection: 'column', gap: 4
            }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 2 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            navigate('/');
                        }}
                    >
                        Wróć
                    </Button>
                </Box>
                <form onSubmit={handleSubmit} style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
                    <HighlightedTextField
                        variant="outlined"
                        fullWidth
                        id="nip"
                        name="nip"
                        label="NIP"
                        value={customer.nip}
                        onChange={handleChange}
                        error={!!errors.nip}
                        helperText={errors.nip}
                    />
                    <TextField
                        variant="outlined"
                        fullWidth
                        id="companyName"
                        name="companyName"
                        label="Nazwa firmy"
                        value={customer.companyName}
                        onChange={handleChange}
                        error={!!errors.companyName}
                        helperText={errors.companyName}
                    />
                    <TextField
                        variant="outlined"
                        fullWidth
                        id="companyOwnerName"
                        name="companyOwnerName"
                        label="Imię właściciela firmy"
                        value={customer.companyOwnerName}
                        onChange={handleChange}
                        error={!!errors.companyOwnerName}
                        helperText={errors.companyOwnerName}
                    />
                    <TextField
                        variant="outlined"
                        fullWidth
                        id="companyOwnerSurname"
                        name="companyOwnerSurname"
                        label="Nazwisko właściciela firmy"
                        value={customer.companyOwnerSurname}
                        onChange={handleChange}
                        error={!!errors.companyOwnerSurname}
                        helperText={errors.companyOwnerSurname}
                    />
                    <Button
                        variant="outlined"
                        color="secondary"
                        onClick={fillFromNipInfo}
                        style={{ gridColumn: 'span 2' }}
                    >
                        Pobierz dane z GUS
                    </Button>
                    <TextField
                        variant="outlined"
                        fullWidth
                        id="name"
                        name="name"
                        label="Imię"
                        value={customer.name}
                        onChange={handleChange}
                        error={!!errors.name}
                        helperText={errors.name}
                    />
                    <TextField
                        variant="outlined"
                        fullWidth
                        id="surname"
                        name="surname"
                        label="Nazwisko"
                        value={customer.surname}
                        onChange={handleChange}
                        error={!!errors.surname}
                        helperText={errors.surname}
                    />

                    <TextField
                        variant="outlined"
                        fullWidth
                        id="companyStreetName"
                        name="companyStreetName"
                        label="Ulica firmy"
                        value={customer.companyAddress.streetName}
                        onChange={(e) => {
                            setCustomerAndValidate({
                                ...customer,
                                companyAddress: { ...customer.companyAddress, streetName: e.target.value },
                            });
                        }}
                        error={!!errors.companyStreetName}
                        helperText={errors.companyStreetName}
                    />
                    <TextField
                        variant="outlined"
                        fullWidth
                        id="companyStreetNumber"
                        name="companyStreetNumber"
                        label="Numer budynku firmy"
                        value={customer.companyAddress.streetNumber}
                        onChange={(e) => {
                            setCustomerAndValidate({
                                ...customer,
                                companyAddress: {
                                    ...customer.companyAddress,
                                    streetNumber: e.target.value,
                                },
                            });
                        }}
                        error={!!errors.companyStreetNumber}
                        helperText={errors.companyStreetNumber}
                    />
                    <TextField
                        variant="outlined"
                        fullWidth
                        id="companyHouseNumber"
                        name="companyHouseNumber"
                        label="Numer lokalu firmy"
                        value={customer.companyAddress.houseNumber}
                        onChange={(e) => {
                            setCustomerAndValidate({
                                ...customer,
                                companyAddress: {
                                    ...customer.companyAddress,
                                    houseNumber: e.target.value,
                                },
                            });
                        }}
                    />
                    <TextField
                        variant="outlined"
                        fullWidth
                        id="companyZipCode"
                        name="companyZipCode"
                        label="Kod pocztowy firmy"
                        value={customer.companyAddress.zipCode}
                        onChange={(e) => {
                            setCustomerAndValidate({
                                ...customer,
                                companyAddress: { ...customer.companyAddress, zipCode: e.target.value },
                            });
                        }}
                        error={!!errors.companyZipCode}
                        helperText={errors.companyZipCode}
                    />
                    <TextField
                        variant="outlined"
                        fullWidth
                        id="companyCity"
                        name="companyCity"
                        label="Miasto firmy"
                        value={customer.companyAddress.city}
                        onChange={(e) => {
                            setCustomerAndValidate({
                                ...customer,
                                companyAddress: { ...customer.companyAddress, city: e.target.value },
                            });
                        }}
                        error={!!errors.companyCity}
                        helperText={errors.companyCity}
                    />
                    <TextField
                        variant="outlined"
                        fullWidth
                        id="companyVoivodeship"
                        name="companyVoivodeship"
                        label="Województwo Firmy"
                        value={customer.companyAddress.voivodeship}
                        onChange={(e) => {
                            setCustomerAndValidate({
                                ...customer,
                                companyAddress: { ...customer.companyAddress, voivodeship: e.target.value },
                            });
                        }}
                        error={!!errors.voivodeship}
                        helperText={errors.voivodeship}
                    />
                    <TextField
                        variant="outlined"
                        fullWidth
                        id="companyDistrict"
                        name="companyDistrict"
                        label="Powiat Firmy"
                        value={customer.companyAddress.district}
                        onChange={(e) => {
                            setCustomerAndValidate(({
                                ...customer,
                                companyAddress: { ...customer.companyAddress, district: e.target.value },
                            }));
                        }}
                        error={!!errors.district}
                        helperText={errors.district}
                    />
                    <TextField
                        variant="outlined"
                        fullWidth
                        id="companyMunicipality"
                        name="companyMunicipality"
                        label="Gmina Firmy"
                        value={customer.companyAddress.municipality}
                        onChange={(e) => {
                            setCustomerAndValidate(({
                                ...customer,
                                companyAddress: { ...customer.companyAddress, municipality: e.target.value },
                            }));
                        }}
                        error={!!errors.district}
                        helperText={errors.district}
                    />
                    <TextField
                        variant="outlined"
                        fullWidth
                        id="companyMunicipalityCode"
                        name="companyMunicipalityCode"
                        label="Kod Gminy Firmy"
                        value={customer.companyAddress.municipalityCode}
                        onChange={(e) => {
                            setCustomerAndValidate({
                                ...customer,
                                companyAddress: { ...customer.companyAddress, municipalityCode: e.target.value },
                            });
                        }
                        }
                        error={!!errors.municipalityCode}
                        helperText={errors.municipalityCode}

                    />
                    <TextField
                        variant="outlined"
                        fullWidth
                        id="regon"
                        name="regon"
                        label="REGON"
                        value={customer.regon}
                        onChange={handleChange}
                        error={!!errors.regon}
                        helperText={errors.regon}
                    />
                    <TextField
                        variant="outlined"
                        fullWidth
                        id="pkd"
                        name="pkd"
                        label="PKD"
                        value={customer.pkd}
                        onChange={handleChange}
                        error={!!errors.pkd}
                        helperText={errors.pkd}
                    />
                    <DatePicker
                        name="companyStartDate"
                        label="Data rozpoczęcia działalności"
                        value={dayjs(customer.companyStartDate)}
                        onChange={(newValue) => setCustomerAndValidate({ ...customer, companyStartDate: newValue?.format('YYYY-MM-DD') || '' })}
                    />
                    <DatePicker
                        name="companyCreationDate"
                        label="Data założenia firmy"
                        value={dayjs(customer.companyCreationDate)}
                        onChange={(newValue) => setCustomerAndValidate({ ...customer, companyCreationDate: newValue?.format('YYYY-MM-DD') || '' })}
                    />
                    <Box sx={{ gridColumn: '1 / -1', height: 50 }} />
                    <HighlightedTextField
                        variant="outlined"
                        fullWidth
                        id="pesel"
                        name="pesel"
                        label="PESEL"
                        value={customer.pesel}
                        onChange={(e) => {
                            setCustomerAndValidate(({
                                ...customer,
                                pesel: e.target.value,
                            }));
                        }}
                        error={!!errors.pesel}
                        helperText={errors.pesel}
                    />
                    <DatePicker
                        name="birthDate"
                        label="Data urodzenia"
                        value={dayjs(customer.birthDate)}
                        onChange={(newValue) => setCustomerAndValidate({ ...customer, birthDate: newValue?.format('YYYY-MM-DD') || '' })}
                    />
                    <TextField
                        variant="outlined"
                        fullWidth
                        id="sex"
                        name="sex"
                        label="Płeć"
                        value={customer.sex}
                        onChange={handleChange}
                        error={!!errors.sex}
                        helperText={errors.sex}
                    />
                    <FormControl fullWidth>
                        <InputLabel id="education-label">Wykształcenie</InputLabel>
                        <Select
                            labelId="education-label"
                            id="education"
                            name="education"
                            value={customer.education}
                            label="Wykształcenie"
                            onChange={e => {
                                setCustomerAndValidate({
                                    ...customer,
                                    education: e.target.value,
                                });
                            }}
                            error={!!errors.education}
                        >
                            {educationValues.map(option => (
                                <MenuItem key={option} value={option}>{option}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <HighlightedTextField
                        variant="outlined"
                        fullWidth
                        id="phoneNumber"
                        name="phoneNumber"
                        label="Numer telefonu"
                        value={customer.phoneNumber}
                        onChange={handleChange}
                        error={!!errors.phoneNumber}
                        helperText={errors.phoneNumber}
                    />
                    <HighlightedTextField
                        variant="outlined"
                        fullWidth
                        id="email"
                        name="email"
                        label="Email"
                        value={customer.email}
                        onChange={handleChange}
                        error={!!errors.email}
                        helperText={errors.email}
                    />
                    <HighlightedTextField
                        variant="outlined"
                        fullWidth
                        id="accountNumber"
                        name="accountNumber"
                        label="Numer konta"
                        value={customer.accountNumber}
                        onChange={(e) => {
                            setCustomerAndValidate(({
                                ...customer,
                                accountNumber: e.target.value,
                            }));
                        }}
                        error={!!errors.accountNumber}
                        helperText={errors.accountNumber}
                    />
                    <TextField
                        variant="outlined"
                        fullWidth
                        id="bankName"
                        name="bankName"
                        label="Nazwa banku"
                        value={customer.bankName}
                        onChange={handleChange}
                        error={!!errors.bankName}
                        helperText={errors.bankName}
                    />
                    <TextField
                        variant="outlined"
                        fullWidth
                        id="streetName"
                        name="streetName"
                        label="Ulica"
                        value={customer.address.streetName}
                        onChange={(e) => {
                            setCustomerAndValidate({
                                ...customer,
                                address: { ...customer.address, streetName: e.target.value },
                            });
                        }}
                        error={!!errors.streetName}
                        helperText={errors.streetName}
                    />
                    <TextField
                        variant="outlined"
                        fullWidth
                        id="streetNumber"
                        name="streetNumber"
                        label="Numer ulicy"
                        value={customer.address.streetNumber}
                        onChange={(e) => {
                            setCustomerAndValidate({
                                ...customer,
                                address: { ...customer.address, streetNumber: e.target.value },
                            });
                        }}
                        error={!!errors.streetNumber}
                        helperText={errors.streetNumber}
                    />
                    <TextField
                        variant="outlined"
                        fullWidth
                        id="zipCode"
                        name="zipCode"
                        label="Kod pocztowy"
                        value={customer.address.zipCode}
                        onChange={(e) => {
                            setCustomerAndValidate({
                                ...customer,
                                address: { ...customer.address, zipCode: e.target.value },
                            });
                        }}
                        error={!!errors.zipCode}
                        helperText={errors.zipCode}
                    />
                    <TextField
                        variant="outlined"
                        fullWidth
                        id="city"
                        name="city"
                        label="Miasto"
                        value={customer.address.city}
                        onChange={(e) => {
                            setCustomerAndValidate({
                                ...customer,
                                address: { ...customer.address, city: e.target.value },
                            });
                        }}
                        error={!!errors.city}
                        helperText={errors.city}
                    />
                    <TextField
                        variant="outlined"
                        fullWidth
                        id="voivodeship"
                        name="voivodeship"
                        label="Województwo"
                        value={customer.address.voivodeship}
                        onChange={(e) => {
                            setCustomerAndValidate({
                                ...customer,
                                address: { ...customer.address, voivodeship: e.target.value },
                            });
                        }}
                        error={!!errors.voivodeship}
                        helperText={errors.voivodeship}
                    />
                    <TextField
                        variant="outlined"
                        fullWidth
                        id="district"
                        name="district"
                        label="Powiat"
                        value={customer.address.district}
                        onChange={(e) => {
                            setCustomerAndValidate(({
                                ...customer,
                                address: { ...customer.address, district: e.target.value },
                            }));
                        }}
                        error={!!errors.district}
                        helperText={errors.district}
                    />
                    <TextField
                        variant="outlined"
                        fullWidth
                        id="municipality"
                        name="municipality"
                        label="Gmina"
                        value={customer.address.municipality}
                        onChange={(e) => {
                            setCustomerAndValidate(({
                                ...customer,
                                address: { ...customer.address, municipality: e.target.value },
                            }));
                        }}
                        error={!!errors.district}
                        helperText={errors.district}
                    />
                    <TextField
                        variant="outlined"
                        fullWidth
                        id="municipalityCode"
                        name="municipalityCode"
                        label="Kod gminy"
                        value={customer.address.municipalityCode}
                        onChange={(e) => {
                            setCustomerAndValidate({
                                ...customer,
                                address: { ...customer.address, municipalityCode: e.target.value },
                            });
                        }
                        }
                        error={!!errors.municipalityCode}
                        helperText={errors.municipalityCode}

                    />
                    <FormControl fullWidth>
                        <InputLabel id="settlement-label">Sposób rozliczenia</InputLabel>
                        <Select
                            labelId="settlement-label"
                            id="settlement"
                            name="settlement"
                            value={customer.settlement}
                            label="Sposób rozliczenia"
                            onChange={e => {
                                setCustomerAndValidate({
                                    ...customer,
                                    settlement: e.target.value,
                                });
                            }}
                            error={!!errors.settlement}
                        >
                            {settlementValues.map(option => (
                                <MenuItem key={option} value={option}>{option}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControlLabel
                        sx={{ gridColumn: 'span 1' }} // or '1 / -1' if you want it to span the whole row
                        control={
                            <Checkbox
                                color="primary"
                                checked={customer.isDisabled}
                                onChange={(e) =>
                                    setCustomerAndValidate({
                                        ...customer,
                                        isDisabled: e.target.checked,
                                    })
                                }
                            />
                        }
                        label="Niepełnosprawny"
                    />
                    <Button disabled={isLoading} variant="contained" color="secondary" onClick={async () => {
                        setIsLoading(true);
                        try {
                            await api.downloadCard(customer);
                        } catch (error) {
                            console.error(error);
                            enqueueSnackbar('Błąd podczas generowania wizytówki', { variant: 'error' });
                        } finally {
                            setIsLoading(false);
                        }
                    }}>
                        Generuj Wizytówkę
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                    >
                        Zapisz
                    </Button>
                </form>
                <TextField
                    variant="outlined"
                    fullWidth
                    id="notes"
                    name="notes"
                    label="Notatki"
                    value={customer.notes}
                    onChange={handleChange}
                    multiline
                    minRows={4}
                    style={{ gridColumn: '1 / -1' }}
                />
                <EmployeesList employees={customer.employees} setEmployees={(employees) => { setCustomerAndValidate({ ...customer, employees: employees }) }} setAllEmployeesCorrect={setEmployeesValid} employeesChanged={employeesChanged} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography>Szczegóły finansowe - 2024-2025</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <FinancialsForm
                                financials={customer.financials0}
                                modelChanged={financialsModelChanged}
                                setFinancials={(financials) => {
                                    setCustomerAndValidate({
                                        ...customer,
                                        financials0: financials,
                                    });
                                }}
                                setFinancialsValid={(valid) => {
                                    setFinancialsValid(0, valid);
                                }}
                            />
                        </AccordionDetails>
                    </Accordion>
                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography>Szczegóły finansowe - 2023-2024</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <FinancialsForm
                                financials={customer.financials1}
                                modelChanged={financialsModelChanged}
                                setFinancials={(financials) => {
                                    setCustomerAndValidate({
                                        ...customer,
                                        financials1: financials,
                                    });
                                }}
                                setFinancialsValid={(valid) => {
                                    setFinancialsValid(1, valid);
                                }}
                            />
                        </AccordionDetails>
                    </Accordion>
                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography>Szczegóły finansowe - 2022-2023</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <FinancialsForm
                                financials={customer.financials2}
                                modelChanged={financialsModelChanged}
                                setFinancials={(financials) => {
                                    setCustomerAndValidate({
                                        ...customer,
                                        financials2: financials,
                                    });
                                }}
                                setFinancialsValid={(valid) => {
                                    setFinancialsValid(2, valid);
                                }}
                            />
                        </AccordionDetails>
                    </Accordion>
                </Box>

                <Box sx={{ display: 'flex', direction: 'row', gap: '16px' }}>
                    <TextField
                        variant="outlined"
                        fullWidth
                        id="deMinimisPLN"
                        name="deMinimisPLN"
                        label="DE MINIMIS PLN"
                        error={!!errors.deMinimisPLN}
                        helperText={errors.deMinimisPLN}
                        value={deMinimisFields.deMinimisPLN}
                        onChange={(e) => setAndValidateDeMinimis({ deMinimisPLN: e.target.value, deMinimisEUR: deMinimisFields.deMinimisEUR })}
                    />
                    <TextField
                        variant="outlined"
                        fullWidth
                        id="deMinimisEUR"
                        name="deMinimisEUR"
                        label="DE MINIMIS EUR"
                        error={!!errors.deMinimisEUR}
                        helperText={errors.deMinimisEUR}
                        value={deMinimisFields.deMinimisEUR}
                        onChange={(e) => setAndValidateDeMinimis({ deMinimisEUR: e.target.value, deMinimisPLN: deMinimisFields.deMinimisPLN })}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                color="primary"
                                checked={customer.usesDeMinimis}
                                onChange={(e) => {
                                    e.preventDefault();
                                }}
                            />
                        }
                        label="Używa DE MINIMIS"
                    />
                </Box>
                <Box sx={{ display: 'flex', direction: 'row', gap: '16px' }}>
                    <TextField
                        variant="outlined"
                        fullWidth
                        id="login"
                        name="login"
                        label="Login"
                        value={customer.login}
                        onChange={handleChange}
                    />
                    <TextField
                        variant="outlined"
                        fullWidth
                        id="password"
                        name="password"
                        label="Hasło"
                        value={customer.password}
                        onChange={handleChange}
                    />
                    <TextField
                        variant="outlined"
                        fullWidth
                        id="autodiagnosisCode"
                        name="autodiagnosisCode"
                        label="Kod autodiagnozy"
                        value={customer.autodiagnosisCode}
                        onChange={handleChange}
                    />
                </Box>
                <FormControlLabel
                    control={
                        <Checkbox
                            color="primary"
                            checked={customer.dataComplete}
                            onChange={(e) => setCustomerAndValidate({ ...customer, dataComplete: e.target.checked })}
                        />
                    }
                    label="Dane klienta kompletne"
                />
            </Box >
        </>
    );
};

export default CustomerAddPage;